
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Target, Calendar, Sparkles } from 'lucide-react';

interface TreatmentProgressProps {
  completed: number;
  goal: number;
  percentage: number;
}

const TreatmentProgress = ({ completed, goal, percentage }: TreatmentProgressProps) => {
  const isGoalReached = completed >= goal;

  return (
    <Card className="relative overflow-hidden bg-white/90 backdrop-blur-luxury border border-juv-rose-clay/50 shadow-rose-clay">
      <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-juv-rose-clay/20 to-transparent rounded-full blur-2xl"></div>
      
      <CardHeader className="relative z-10">
        <CardTitle className="flex items-center space-x-2">
          <Target className="h-5 w-5 text-juv-terracotta" />
          <span>Monthly Glow Goal</span>
          {isGoalReached && <Sparkles className="h-5 w-5 text-juv-terracotta animate-pulse" />}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Progress this month</span>
          <span className="text-sm font-medium text-gray-900">
            {completed} of {goal} treatments
          </span>
        </div>
        
        <Progress value={Math.min(percentage, 100)} className="h-3 bg-gray-200">
          <div 
            className={`h-full rounded-full transition-all duration-700 ease-out ${
              isGoalReached 
                ? 'bg-gradient-to-r from-green-400 to-green-500' 
                : 'bg-juv-gradient-primary'
            }`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </Progress>
        
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-gray-600">
              {goal - completed > 0 ? `${goal - completed} more to go` : 'Goal achieved! 🎉'}
            </span>
          </div>
          <span className={`font-medium ${isGoalReached ? 'text-green-600' : 'text-juv-terracotta'}`}>
            {Math.round(percentage)}%
          </span>
        </div>
        
        {isGoalReached && (
          <div className="mt-4 p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
            <p className="text-green-800 text-sm font-medium text-center">
              🎉 Congratulations! You've reached your monthly goal!
            </p>
          </div>
        )}
        
        {percentage > 100 && (
          <div className="mt-2 p-3 bg-juv-gradient-secondary rounded-lg border border-juv-rose-clay">
            <p className="text-juv-deep-chestnut text-sm font-medium text-center">
              ✨ You're exceeding expectations! Keep glowing!
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TreatmentProgress;
